import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IMAGE_MANAGER_DIALOG_DATA } from '../../img-modal.component';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
    ImageItem,
    MODULES_OPTIONS,
    PEXELS_COLORS,
    PEXELS_FILTER,
    PEXELS_ORIENTATION,
    PEXELS_PHOTO,
    PEXELS_PHOTO_SRC,
} from '@app/_models/images-manager/images-management.model';
import { Select, Store } from '@ngxs/store';
import { debounceTime, Observable } from 'rxjs';
import { McToastService } from '@app/_services';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { CarouselModalComponent } from '../carousel-modal/carousel-modal.component';
import { GetPexelsData, SetPexelsFilter, UploadPhoto } from '../../store/images.actions';
import { ImagesState } from '../../store/images.state';
import * as _ from 'lodash';
import { environment } from '@env/environment.local';

@Component({
    selector: 'pexels-upload',
    templateUrl: 'pexels-upload.component.html',
    styleUrls: ['pexels-upload.component.scss'],
})
export class PexelsUploadComponent implements OnInit, OnDestroy {
    assetsUrl: string = environment.assets_url;
    mfsPath: string = environment.mfs_path;
    @Input() data: IMAGE_MANAGER_DIALOG_DATA;
    @Output() closeDialog: EventEmitter<boolean> = new EventEmitter();
    loading: boolean = false;
    selectedFiles: Set<PEXELS_PHOTO> = new Set();

    pexelsForm: UntypedFormGroup;

    orientationOptions: { id: PEXELS_ORIENTATION; name: string }[] = [];

    colorOptions: { id: PEXELS_COLORS; name: string }[] = [];

    @Select(ImagesState.getPexelsImages)
    pexelsImages$: Observable<PEXELS_PHOTO[]>;

    pexelsImages: PEXELS_PHOTO[] = [];

    @Select(ImagesState.getPexelsNextUrl)
    pexelsNextUrl$: Observable<string>;

    pexelsNextUrl: string;

    @Select(ImagesState.getPexelsFilter)
    pexelsFilter$: Observable<PEXELS_FILTER>;

    pexelsFilter: PEXELS_FILTER;

    filterColor: PEXELS_COLORS = PEXELS_COLORS.NONE;

    pexelsColors = PEXELS_COLORS;

    appliedFilters: boolean = false;

    @Select(ImagesState.getShowPexelsEmptyState)
    showEmptyState$: Observable<boolean>;

    loadingGif: string = '';

    selectedSize: string = 'medium';
    selectedSizeTranslated: string = '';
    sizes: string[] = ['landscape', 'large', 'large2x', 'medium', 'original', 'portrait', 'small', 'tiny'];

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private toast: McToastService,
        private translate: TranslateService,
        private dialog: MatDialog
    ) {
        this.pexelsFilter$.subscribe((filter) => {
            this.pexelsFilter = filter;
        });

        this.orientationOptions = [
            {
                id: PEXELS_ORIENTATION.NONE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.orientacion.seleccione'
                )}`,
            },
            {
                id: PEXELS_ORIENTATION.LANDSCAPE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.orientacion.landscape'
                )}`,
            },
            {
                id: PEXELS_ORIENTATION.PORTRAIT,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.orientacion.portrait'
                )}`,
            },
            {
                id: PEXELS_ORIENTATION.SQUARE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.orientacion.square'
                )}`,
            },
        ];

        this.colorOptions = [
            {
                id: PEXELS_COLORS.RED,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.red'
                )}`,
            },
            {
                id: PEXELS_COLORS.ORANGE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.orange'
                )}`,
            },
            {
                id: PEXELS_COLORS.YELLOW,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.yellow'
                )}`,
            },
            {
                id: PEXELS_COLORS.GREEN,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.green'
                )}`,
            },
            {
                id: PEXELS_COLORS.TURQUOISE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.turquoise'
                )}`,
            },
            {
                id: PEXELS_COLORS.BLUE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.blue'
                )}`,
            },
            {
                id: PEXELS_COLORS.VIOLET,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.violet'
                )}`,
            },
            {
                id: PEXELS_COLORS.PINK,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.pink'
                )}`,
            },
            {
                id: PEXELS_COLORS.BROWN,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.brown'
                )}`,
            },
            {
                id: PEXELS_COLORS.BLACK,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.black'
                )}`,
            },
            {
                id: PEXELS_COLORS.GRAY,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.gray'
                )}`,
            },
            {
                id: PEXELS_COLORS.WHITE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.upload_image_modal.pexels.filters.colores.nombres.white'
                )}`,
            },
        ];

        this.pexelsImages$.subscribe((data: PEXELS_PHOTO[]) => {
            if (data) {
                if (this.appliedFilters) {
                    this.pexelsImages = data;
                    this.appliedFilters = false;
                } else {
                    this.pexelsImages = this.pexelsImages.concat(data);
                }
            }
        });

        this.pexelsNextUrl$.subscribe((data) => {
            this.pexelsNextUrl = data;
        });
    }
    ngOnDestroy(): void {
        const filter: PEXELS_FILTER = {
            ep_key: 'curated_photos',
            args: {
                per_page: 20,
                page: 1,
            },
        };
        this.store.dispatch(new SetPexelsFilter(filter));
    }

    ngOnInit(): void {
        this.buildForm();
        this.setSelectedSizeLabel();
        this.loading = true;
        this.store.dispatch(new GetPexelsData()).subscribe({
            complete: () => {
                this.loading = false;
                this.loadingGif = 'img/front/images/illustration_download.gif';
            },
            error: (error) => (this.loading = false),
        });
    }

    buildForm() {
        this.pexelsForm = this.fb.group({
            searchValue: [''],
            color: [this.colorOptions[0].id],
            orientation: [this.orientationOptions[0].id],
            size: [this.sizes[3]],
        });

        this.pexelsForm
            .get('searchValue')
            .valueChanges.pipe(debounceTime(1000))
            .subscribe((value) => {
                this.updateFilter();
            });

        this.pexelsForm.get('orientation').valueChanges.subscribe((value) => {
            this.updateFilter();
        });
    }

    toggleFileSelection(photo: PEXELS_PHOTO): void {
        if (this.selectedFiles.has(photo)) {
            this.selectedFiles.delete(photo);
        } else {
            if (this.selectedFiles.size === 15) {
                this.toast.danger(this.translate.instant('app.producto.toast.error_max_imagen', { limit: 15 }));
                return;
            }
            this.selectedFiles.add(photo);
        }
    }

    reset(): void {
        if (this.selectedFiles.size >= 1) {
            this.selectedFiles.clear();
        } else {
            this.closeDialog.emit(true);
        }
    }

    openCarouselModal(index: number): void {
        const images: ImageItem[] = [];
        this.pexelsImages.forEach((photo: PEXELS_PHOTO, index) => {
            const item: ImageItem = {
                id: index,
                filetype: 'image',
                filepath: photo.src.medium,
                filename: photo.src.medium,
                module: MODULES_OPTIONS.ALL,
                relation_id: this.data.relation_id.toString(),
                url: photo.src.medium,
                size: photo.src.medium.length,
                use_module: [],
            };
            images.push(item);
        });
        this.dialog.open(CarouselModalComponent, {
            data: { images, index },
            width: '80%',
            panelClass: 'full-image-modal',
        });
    }

    onScroll(event: any) {
        if (
            Math.ceil(event.target.offsetHeight) + Math.ceil(event.target.scrollTop) >= event.target.scrollHeight &&
            this.pexelsNextUrl
        ) {
            this.loading = true;

            const filter = {
                ...this.pexelsFilter,
            };

            filter.args.page++;

            this.store.dispatch(new SetPexelsFilter(filter)).subscribe({
                complete: () => {
                    this.store.dispatch(new GetPexelsData()).subscribe({
                        complete: () => (this.loading = false),
                        error: (error) => (this.loading = false),
                    });
                },
            });
        }
    }

    selectSize(size: string) {
        this.selectedSize = size;
        this.setSelectedSizeLabel();
    }

    setSelectedSizeLabel() {
        const key = `app.global.modals.image_manager.upload_image_modal.pexels.buttons.size.${this.selectedSize}`;
        this.translate.get(key).subscribe((translated) => {
            this.selectedSizeTranslated = translated;
        });
    }

    upload(): void {
        this.loading = true;
        const formData: FormData = new FormData();

        let formIndex = 0;
        const selectedFiles = Array.from(this.selectedFiles);

        selectedFiles.forEach((photo: any) => {
            const file = photo.src[this.selectedSize];
            formData.append(`data[${formIndex}][file]`, file);
            formData.append(`data[${formIndex}][relation_id]`, this.data.relation_id.toString());
            formData.append(`data[${formIndex}][module]`, this.data.module);
            formData.append(`data[${formIndex}][type]`, 'image');
            formData.append(`data[${formIndex}][url]`, 'true');
            formIndex++;
        });

        this.store.dispatch(new UploadPhoto(formData)).subscribe({
            complete: () => (this.loading = false),
            error: () => (this.loading = false),
        });
    }

    updateColorFilter(color: PEXELS_COLORS): void {
        this.filterColor = color;
        this.updateFilter();
    }

    getPexelColorData(): { id: PEXELS_COLORS; name: string } {
        return this.colorOptions.find((o) => o.id === this.filterColor);
    }

    updateFilter(): void {
        this.loading = true;
        this.pexelsImages = [];
        const orientation = this.pexelsForm.get('orientation').value;
        const searchValue = this.pexelsForm.get('searchValue').value;

        let filter = {
            ...this.pexelsFilter,
        };

        if (this.filterColor === 'none' || this.filterColor === 'all') {
            filter.args = _.omit(filter.args, ['color']);
        } else {
            filter.args.color = this.filterColor;
        }

        if (orientation === PEXELS_ORIENTATION.NONE) {
            filter.args = _.omit(filter.args, ['orientation']);
        } else {
            filter.args.orientation = orientation;
        }

        filter.args.query = searchValue ?? '';

        if (
            (this.filterColor === 'none' || this.filterColor === 'all') &&
            orientation === PEXELS_ORIENTATION.NONE &&
            !searchValue?.length
        ) {
            filter.ep_key = 'curated_photos';
            this.appliedFilters = false;
        } else {
            filter.ep_key = 'search_photos';
            this.appliedFilters = true;
        }

        filter.args.page = 1;

        this.store.dispatch(new SetPexelsFilter(filter)).subscribe({
            complete: () => {
                this.store.dispatch(new GetPexelsData()).subscribe({
                    complete: () => {
                        this.loading = false;
                    },
                    error: (error) => {
                        this.loading = false;
                        this.appliedFilters = false;
                    },
                });
            },
        });
    }

    clearSearchInput(): void {
        this.pexelsForm.get('searchValue').setValue('');
    }

    getUser(userUrl: string): string {
        return `@${userUrl.split('@')[1]}`;
    }

    getPhotoUrl(src: PEXELS_PHOTO_SRC): string {
        const orientation = this.pexelsForm.get('orientation').value;
        let url = src.tiny;
        switch (orientation) {
            case PEXELS_ORIENTATION.NONE:
                url = src.tiny;
                break;

            case PEXELS_ORIENTATION.LANDSCAPE:
                url = src.landscape;
                break;

            case PEXELS_ORIENTATION.PORTRAIT:
                url = src.portrait;
                break;

            case PEXELS_ORIENTATION.SQUARE:
                url = src.tiny;
                break;

            default:
                url = src.tiny;
                break;
        }
        return url;
    }
}
